import React from "react";
import { useTranslation } from "react-i18next";

/**
 * Shared reviewed-status marker (AKL-696). A small green check badge shown on
 * review-feed grid tiles and full-screen filmstrip thumbnails for assets whose
 * `review_status` is "reviewed". Pair it with {@link REVIEWED_IMAGE_FILTER} to
 * dim/desaturate the thumbnail so unreviewed assets stand out. Styled for light
 * and dark mode; positioned absolutely inside a `position: relative` parent.
 *
 * Accessibility: on a surface with no text label of its own (the grid tile) leave
 * `ariaHidden` false so the badge announces "Reviewed"; on a surface whose parent
 * control already carries the reviewed state in its label (the filmstrip button)
 * pass `ariaHidden` so the state is announced exactly once.
 */

/**
 * CSS `filter` chain that dims and slightly desaturates a reviewed thumbnail
 * image. Applied to the `<img>` only (never the badge or bounding boxes), so
 * boxes stay crisp and the badge stays fully opaque.
 */
export const REVIEWED_IMAGE_FILTER = "grayscale(0.25) opacity(0.7)";

type BadgeCorner = "top-left" | "top-right" | "bottom-left" | "bottom-right";

interface ReviewedBadgeProps {
  /**
   * Corner to pin to. Defaults to top-left — frame-label badges occupy the
   * top-right of the overlay, so the review badge avoids that corner.
   */
  position?: BadgeCorner;
  /** Diameter of the badge in px. Defaults to 20 (readable on the small filmstrip thumbnail). */
  size?: number;
  /**
   * When true the badge is decorative (`aria-hidden`) — use it where the parent
   * control's accessible name already conveys the reviewed state.
   */
  ariaHidden?: boolean;
  className?: string;
}

const POSITION_CLASS: Record<BadgeCorner, string> = {
  "top-left": "top-1 left-1",
  "top-right": "top-1 right-1",
  "bottom-left": "bottom-1 left-1",
  "bottom-right": "bottom-1 right-1",
};

/**
 * Renders the reviewed check badge.
 * @param position corner to pin the badge to (default "top-left")
 * @param size badge diameter in px (default 20)
 * @param ariaHidden hide from assistive tech when the parent announces the state
 * @param className extra classes to merge onto the badge
 */
const ReviewedBadge: React.FC<ReviewedBadgeProps> = ({
  position = "top-left",
  size = 20,
  ariaHidden = false,
  className = "",
}) => {
  const { t } = useTranslation();
  const label = t("Reviewed");

  return (
    // z-[1001]: sit above BoundingBoxOverlay's active/hovered box (zIndex 1000)
    // so an overlapping detection box can never cover the badge on grid tiles.
    <span
      className={`pointer-events-none absolute ${POSITION_CLASS[position]} z-[1001] flex items-center justify-center rounded-full bg-green-600 text-white shadow ring-2 ring-white dark:ring-black/40 ${className}`}
      style={{ width: size, height: size }}
      // No `title`: the badge is pointer-events-none (clicks pass through to the
      // tile/button), so a native tooltip can never show. `aria-label` drives the
      // accessible name and is unaffected by pointer-events.
      aria-label={ariaHidden ? undefined : label}
      role={ariaHidden ? undefined : "img"}
      aria-hidden={ariaHidden || undefined}
    >
      <svg
        width={size * 0.6}
        height={size * 0.6}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth={3}
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <polyline points="20 6 9 17 4 12" />
      </svg>
    </span>
  );
};

export default ReviewedBadge;
